import "../../css/blocks/contactform.css";
import type { ContactFormData, ContactFormElements } from "../types/contact-form";
import { FormValidator } from "../modules/form-validator";
import { FormUI } from "../modules/form-ui";
import { FormSubmitter } from "../modules/form-submitter";
import { getRecaptchaToken } from "../modules/recaptcha";
import { initFormConsentOverlay } from "../modules/gdpr-consent";

class ContactFormHandler {
  private form: HTMLFormElement;
  private elements: ContactFormElements;
  private isSubmitting = false;
  private readonly debounceTime = 2000;
  private readonly validator = new FormValidator();
  private readonly ui = new FormUI();
  private readonly submitter = new FormSubmitter();
  private recaptchaToken = "";

  constructor(form: HTMLFormElement) {
    this.form = form;
    this.elements = this.getFormElements();
    this.init();
  }

  private init(): void {
    this.form.addEventListener("submit", this.handleSubmit.bind(this));
    this.addFieldValidation();
    this.ui.storeOriginalButtonText(this.elements.submitButton);
    this.initConsentCheck();
  }

  private initConsentCheck(): void {
    initFormConsentOverlay(this.form);
  }

  private getFormElements(): ContactFormElements {
    const formId = this.form.id;

    return {
      form: this.form,
      submitButton: this.form.querySelector(".contact-form-submit"),
      messageDiv: this.form.querySelector(".form-message"),
      fields: {
        name: this.form.querySelector(`#${formId}-name`) as HTMLInputElement,
        email: this.form.querySelector(`#${formId}-email`) as HTMLInputElement,
        message: this.form.querySelector(
          `#${formId}-message`,
        ) as HTMLTextAreaElement,
        company: this.form.querySelector(
          `#${formId}-company`,
        ) as HTMLInputElement,
        phone: this.form.querySelector(`#${formId}-phone`) as HTMLInputElement,
      },
    };
  }

  private addFieldValidation(): void {
    this.elements.fields.email?.addEventListener("blur", () => {
      const email = this.elements.fields.email.value;
      if (email.length > 0 && !this.validator.validateEmail(email)) {
        this.ui.showFieldError(
          this.elements.fields.email,
          "Bitte geben Sie eine gültige E-Mail-Adresse ein."
        );
      } else {
        this.ui.clearFieldError(this.elements.fields.email);
      }
    });

    this.elements.fields.message?.addEventListener("input", () => {
      this.ui.updateCharacterCount(this.elements.fields.message, 5000);
    });
  }


  private async handleSubmit(e: Event): Promise<void> {
    e.preventDefault();

    if (this.isSubmitting) return;

    if (!this.validateForm()) return;

    this.isSubmitting = true;
    this.ui.setLoadingState(this.form, this.elements.submitButton, true);

    try {
      this.recaptchaToken = await getRecaptchaToken("contact_form");
      const formData = this.getFormData();
      const response = await this.submitter.submit(formData);

      if (response.success) {
        this.handleSuccess();
      } else {
        this.ui.showMessage(
          this.elements.messageDiv,
          response.data || "Ein Fehler ist aufgetreten.",
          "error"
        );
      }
    } catch (error) {
      this.ui.showMessage(
        this.elements.messageDiv,
        "Netzwerkfehler. Bitte versuchen Sie es später erneut.",
        "error"
      );
      console.error("Form submission error:", error);
    } finally {
      this.ui.setLoadingState(this.form, this.elements.submitButton, false);
      setTimeout(() => {
        this.isSubmitting = false;
      }, this.debounceTime);
    }
  }

  private validateForm(): boolean {
    this.ui.clearAllErrors(this.form);

    const fields = [
      { name: 'name', element: this.elements.fields.name },
      { name: 'email', element: this.elements.fields.email },
      { name: 'message', element: this.elements.fields.message }
    ];

    let isValid = true;

    for (const field of fields) {
      const error = this.validator.getFieldError(field.name, field.element.value);
      if (error) {
        this.ui.showFieldError(field.element, error);
        isValid = false;
      }
    }

    return isValid;
  }

  private getFormData(): ContactFormData {
    return {
      name: this.elements.fields.name.value,
      email: this.elements.fields.email.value,
      message: this.elements.fields.message.value,
      company: this.elements.fields.company?.value || "",
      phone: this.elements.fields.phone?.value || "",
      nonce: window.certlex_ajax?.nonce || "",
      action: "certlex_contact_form_submit",
      recaptcha_token: this.recaptchaToken,
    };
  }


  private handleSuccess(): void {
    const successMessage =
      this.form.dataset.successMessage ||
      "Vielen Dank für Ihre Nachricht. Wir werden uns schnellstmöglich bei Ihnen melden.";

    this.ui.showMessage(this.elements.messageDiv, successMessage, "success");
    this.form.reset();
    this.ui.clearAllErrors(this.form);

    this.elements.messageDiv?.scrollIntoView({
      behavior: "smooth",
      block: "nearest",
    });

    this.submitter.trackConversion();
  }
}

function initializeContactForms(): void {
  const forms = document.querySelectorAll(".contact-form") as NodeListOf<HTMLFormElement>;

  forms.forEach((form) => {
    if (!form.hasAttribute("data-initialized")) {
      new ContactFormHandler(form);
      form.setAttribute("data-initialized", "true");
    }
  });
}

if (document.readyState === "loading") {
  document.addEventListener("DOMContentLoaded", initializeContactForms);
} else {
  initializeContactForms();
}

if (window.wp?.domReady) {
  window.wp.domReady(() => {
    const observer = new MutationObserver((mutations) => {
      mutations.forEach((mutation) => {
        mutation.addedNodes.forEach((node) => {
          if (node instanceof HTMLElement) {
            const forms = node.querySelectorAll(".contact-form") as NodeListOf<HTMLFormElement>;
            forms.forEach((form) => {
              if (!form.hasAttribute("data-initialized")) {
                new ContactFormHandler(form);
                form.setAttribute("data-initialized", "true");
              }
            });
          }
        });
      });
    });

    const editor = document.querySelector(".block-editor-writing-flow");
    if (editor) {
      observer.observe(editor, { childList: true, subtree: true });
    }
  });
}

export { ContactFormHandler };

